import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Row,
  Select,
  Segmented,
  Table,
  Typography,
  Skeleton,
} from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  useReportsServiceDownloadSalesReportV2,
  useReportsServiceGetSalesReportV2,
} from "api/queries";

const { Title } = Typography;

const SalesReport: React.FC = () => {
  const company = useSelector((state: any) => state.company);
  const [activeTab, setActiveTab] = useState("totalSales");
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);
  const [periodFilter, setPeriodFilter] = useState("Today");
  const [totalSalesPage, setTotalSalesPage] = useState(1);
  const [productSalesPage, setProductSalesPage] = useState(1);
  const [reparationSalesPage, setReparationSalesPage] = useState(1);
  const [topProductsPage, setTopProductsPage] = useState(1);
  const pageSize = 10;

  const { data, isLoading, error, isError, refetch, isFetching } =
    useReportsServiceGetSalesReportV2({
      startDate: dateRange[0].startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      endDate: dateRange[1].endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      totalSalesPage,
      totalSalesPageSize: pageSize,
      productSalesPage,
      productSalesPageSize: pageSize,
      reparationSalesPage,
      reparationSalesPageSize: pageSize,
      topProductsPage,
      topProductsPageSize: pageSize,
    });

  const { mutate: getDownloadUrl, isPending: isDownloadPending } =
    useReportsServiceDownloadSalesReportV2({});

  const handleDownload = () => {
    getDownloadUrl(
      {
        startDate: dateRange[0].format("YYYY-MM-DDTHH:mm:ss"),
        endDate: dateRange[1].format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: { downloadUrl?: string }) => {
          if (response?.downloadUrl) {
            const link = document.createElement("a");
            link.href = response.downloadUrl;
            link.setAttribute("download", "sales_report.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      }
    );
  };

  useEffect(() => {
    setTotalSalesPage(1);
    setProductSalesPage(1);
    setReparationSalesPage(1);
    setTopProductsPage(1);
  }, [dateRange]);

  // Handle period filter change
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();

    switch (value) {
      case "Today":
        setDateRange([now.startOf("day"), now.endOf("day")]);
        break;
      case "This Week":
        setDateRange([now.startOf("week"), now.endOf("week")]);
        break;
      case "This Month":
        setDateRange([now.startOf("month"), now.endOf("month")]);
        break;
      case "This Year":
        setDateRange([now.startOf("year"), now.endOf("year")]);
        break;
      default:
        setDateRange([now.startOf("day"), now.endOf("day")]);
    }
  };

  // Status component for rendering different status badges
  const StatusTag = ({ status }: { status: string }) => {
    if (status === "Paid") {
      return (
        <div
          style={{
            background: "#ecf8f0",
            color: "#52c41a",
            borderRadius: "12px",
            padding: "2px 10px",
            textAlign: "center",
            width: "fit-content",
            fontWeight: 500,
          }}
        >
          {status}
        </div>
      );
    } else if (status === "Partially Paid") {
      return (
        <div
          style={{
            background: "#fff7e6",
            color: "#fa8c16",
            borderRadius: "12px",
            padding: "2px 10px",
            textAlign: "center",
            width: "fit-content",
            fontWeight: 500,
          }}
        >
          {status}
        </div>
      );
    } else {
      return (
        <div
          style={{
            background: "#fff1f0",
            color: "#f5222d",
            borderRadius: "12px",
            padding: "2px 10px",
            textAlign: "center",
            width: "fit-content",
            fontWeight: 500,
          }}
        >
          {status}
        </div>
      );
    }
  };

  // Column definitions for the tables
  const totalSalesColumns = [
    {
      title: "Total Invoices Number",
      dataIndex: "totalInvoices",
      key: "totalInvoices",
    },
    {
      title: "Total Sales of Invoices",
      dataIndex: "totalSales",
      key: "totalSales",
      render: (value: number) =>
        `${company.currencySymbol} ${value.toFixed(2)}`,
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      render: (value: number) =>
        `${company.currencySymbol} ${value.toFixed(2)}`,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
  ];

  const productSalesColumns = [
    {
      title: "Invoice ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Employee",
      dataIndex: "employee",
      key: "employee",
    },
    {
      title: "Client Name",
      dataIndex: "client",
      key: "client",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => <StatusTag status={status} />,
    },
    {
      title: "Total Sale",
      dataIndex: "totalSale",
      key: "totalSale",
      render: (value: number) =>
        `${company.currencySymbol} ${value.toFixed(0)}`,
    },
    {
      title: "Total Paid",
      dataIndex: "totalPaid",
      key: "totalPaid",
      render: (value: number) =>
        `${company.currencySymbol} ${value.toFixed(0)}`,
    },
    {
      title: "Remaining",
      dataIndex: "remaining",
      key: "remaining",
      render: (value: number) =>
        `${company.currencySymbol} ${value.toFixed(0)}`,
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      render: (value: number) =>
        `${company.currencySymbol} ${value.toFixed(0)}`,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
  ];

  const topSellingProductsColumns = [
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Quantity Sold",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      render: (value: number) =>
        `${company.currencySymbol} ${value.toFixed(0)}`,
    },
  ];

  // KPI values
  const totalInvoicedSales = data?.kpis.totalInvoicedSales || 0;
  const cashReceived = data?.kpis.cashReceived || 0;
  const productSalesTotal = data?.kpis.productSalesTotal || 0;
  const reparationSalesTotal = data?.kpis.reparationSalesTotal || 0;
  const accountsReceivable = data?.kpis.accountsReceivable || 0;

  if (isError) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Typography.Title level={4} type="danger">
          Failed to load sales report
        </Typography.Title>
        <Typography.Text type="danger">
          {(error as any)?.body?.message ||
            (error as any)?.response?.data?.message ||
            (error as any)?.message ||
            "An unexpected error occurred."}
        </Typography.Text>
      </div>
    );
  }

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 20 }}>
        <Title level={2} style={{ margin: 0 }}>
          Sales Report
        </Title>
        <div style={{ display: "flex", gap: 10 }}>
          <Select
            value={periodFilter}
            onChange={handlePeriodChange}
            style={{ width: 120 }}
          >
            <Select.Option value="Today">Today</Select.Option>
            <Select.Option value="This Week">This Week</Select.Option>
            <Select.Option value="This Month">This Month</Select.Option>
            <Select.Option value="This Year">This Year</Select.Option>
          </Select>
          <DatePicker
            value={dateRange[0]}
            onChange={(date) => setDateRange([date || dayjs(), dateRange[1]])}
            style={{ width: 120 }}
            placeholder="From"
          />
          <DatePicker
            value={dateRange[1]}
            onChange={(date) => setDateRange([dateRange[0], date || dayjs()])}
            style={{ width: 120 }}
            placeholder="To"
          />
          <Button
            type="primary"
            style={{ background: "#fa8c16", borderColor: "#fa8c16" }}
            onClick={handleDownload}
            loading={isDownloadPending}
          >
            Export Report
          </Button>
        </div>
      </Flex>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 20 }} justify="space-between">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, idx) => (
            <Col span={4.8} style={{ minWidth: "200px" }} key={idx}>
              <Skeleton
                active
                paragraph={false}
                title={{ width: "80%" }}
                style={{ padding: 25, borderRadius: 8 }}
              />
            </Col>
          ))
        ) : (
          <>
            <Col span={4.8} style={{ minWidth: "200px" }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "8px",
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                  borderLeft: "4px solid #fa8c16",
                  width: "100%",
                }}
              >
                <div style={{ color: "#8c8c8c", marginBottom: "8px" }}>
                  Total Invoiced Sales
                </div>
                <div
                  style={{
                    color: "#fa8c16",
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginTop: "18px",
                  }}
                >
                  {company.currencySymbol} {totalInvoicedSales.toFixed(0)}
                </div>
              </div>
            </Col>
            <Col span={4.8} style={{ minWidth: "200px" }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "8px",
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                  borderLeft: "4px solid #fa8c16",
                  width: "100%",
                }}
              >
                <div style={{ color: "#8c8c8c", marginBottom: "8px" }}>
                  Cash Received
                </div>
                <div
                  style={{
                    color: "#fa8c16",
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginTop: "18px",
                  }}
                >
                  {company.currencySymbol} {cashReceived.toFixed(0)}
                </div>
              </div>
            </Col>
            <Col span={4.8} style={{ minWidth: "200px" }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "8px",
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                  borderLeft: "4px solid #fa8c16",
                  width: "100%",
                }}
              >
                <div style={{ color: "#8c8c8c", marginBottom: "8px" }}>
                  Product Sales
                </div>
                <div
                  style={{
                    color: "#fa8c16",
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginTop: "18px",
                  }}
                >
                  {company.currencySymbol} {productSalesTotal.toFixed(0)}
                </div>
              </div>
            </Col>
            <Col span={4.8} style={{ minWidth: "200px" }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "8px",
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                  borderLeft: "4px solid #fa8c16",
                  width: "100%",
                }}
              >
                <div style={{ color: "#8c8c8c", marginBottom: "8px" }}>
                  Reparation Sales
                </div>
                <div
                  style={{
                    color: "#fa8c16",
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginTop: "18px",
                  }}
                >
                  {company.currencySymbol} {reparationSalesTotal.toFixed(0)}
                </div>
              </div>
            </Col>
            <Col span={4.8} style={{ minWidth: "200px" }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: "8px",
                  background: "#fff",
                  border: "1px solid #f0f0f0",
                  borderLeft: "4px solid #fa8c16",
                  width: "100%",
                }}
              >
                <div style={{ color: "#8c8c8c", marginBottom: "8px" }}>
                  Accounts Receivable
                </div>
                <div
                  style={{
                    color: "#fa8c16",
                    fontSize: "18px",
                    fontWeight: "bold",
                    marginTop: "18px",
                  }}
                >
                  {company.currencySymbol} {accountsReceivable.toFixed(0)}
                </div>
              </div>
            </Col>
          </>
        )}
      </Row>

      {/* Segmented Navigation */}
      <div style={{ marginBottom: 16 }}>
        <Segmented
          value={activeTab}
          onChange={(value) => setActiveTab(value as string)}
          options={[
            { label: "Total Sales", value: "totalSales" },
            { label: "Product Sales", value: "productSales" },
            { label: "Reparation Sales", value: "reparationSales" },
            { label: "Top Selling Products", value: "topSellingProducts" },
          ]}
          style={{
            background: "#f5f5f5",
            padding: "4px",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Table Content based on active tab */}
      {activeTab === "totalSales" && (
        <Card>
          <Title level={4}>Total Sales Summary</Title>
          <Table
            columns={totalSalesColumns}
            dataSource={data?.totalSales.items || []}
            rowKey="id"
            loading={isLoading}
            scroll={{ x: 900 }}
            pagination={{
              current: totalSalesPage,
              pageSize,
              total: data?.totalSales.total || 0,
              showTotal: (total, range) =>
                `Showing ${range[0]} to ${range[1]} of ${total} entries`,
              onChange: (page) => setTotalSalesPage(page),
              position: ["bottomRight"],
              showSizeChanger: false,
            }}
            footer={() =>
              data?.totalSales.totals ? (
                <div style={{ fontWeight: "bold" }}>
                  Totals: Invoices: {data.totalSales.totals.totalInvoices},
                  Sales: {company.currencySymbol}
                  {data.totalSales.totals.totalSales}, Revenue:{" "}
                  {company.currencySymbol}
                  {data.totalSales.totals.revenue}
                </div>
              ) : null
            }
          />
        </Card>
      )}

      {activeTab === "productSales" && (
        <Card>
          <Title level={4}>Product Sales Details</Title>
          <Table
            columns={productSalesColumns}
            dataSource={data?.productSales.items || []}
            rowKey="id"
            loading={isLoading}
            scroll={{ x: 900 }}
            pagination={{
              current: productSalesPage,
              pageSize,
              total: data?.productSales.total || 0,
              showTotal: (total, range) =>
                `Showing ${range[0]} to ${range[1]} of ${total} entries`,
              onChange: (page) => setProductSalesPage(page),
              position: ["bottomRight"],
              showSizeChanger: false,
            }}
            footer={() =>
              data?.productSales.totals ? (
                <div style={{ fontWeight: "bold" }}>
                  Totals: Sale: {company.currencySymbol}
                  {data.productSales.totals.totalSale}, Paid:{" "}
                  {company.currencySymbol}
                  {data.productSales.totals.totalPaid}, Remaining:{" "}
                  {company.currencySymbol}
                  {data.productSales.totals.remaining}, Revenue:{" "}
                  {company.currencySymbol}
                  {data.productSales.totals.revenue}
                </div>
              ) : null
            }
          />
        </Card>
      )}

      {activeTab === "reparationSales" && (
        <Card>
          <Title level={4}>Reparation Sales Details</Title>
          <Table
            columns={productSalesColumns}
            dataSource={data?.reparationSales.items || []}
            rowKey="id"
            loading={isLoading}
            scroll={{ x: 900 }}
            pagination={{
              current: reparationSalesPage,
              pageSize,
              total: data?.reparationSales.total || 0,
              showTotal: (total, range) =>
                `Showing ${range[0]} to ${range[1]} of ${total} entries`,
              onChange: (page) => setReparationSalesPage(page),
              position: ["bottomRight"],
              showSizeChanger: false,
            }}
            footer={() =>
              data?.reparationSales.totals ? (
                <div style={{ fontWeight: "bold" }}>
                  Totals: Sale: {company.currencySymbol}
                  {data.reparationSales.totals.totalSale}, Paid:{" "}
                  {company.currencySymbol}
                  {data.reparationSales.totals.totalPaid}, Remaining:{" "}
                  {company.currencySymbol}
                  {data.reparationSales.totals.remaining}, Revenue:{" "}
                  {company.currencySymbol}
                  {data.reparationSales.totals.revenue}
                </div>
              ) : null
            }
          />
        </Card>
      )}

      {activeTab === "topSellingProducts" && (
        <Card>
          <Title level={4}>Top Selling Products</Title>
          <Table
            columns={topSellingProductsColumns}
            dataSource={data?.topSellingProducts.items || []}
            rowKey="id"
            loading={isLoading}
            scroll={{ x: 900 }}
            pagination={{
              current: topProductsPage,
              pageSize,
              total: data?.topSellingProducts.total || 0,
              showTotal: (total, range) =>
                `Showing ${range[0]} to ${range[1]} of ${total} entries`,
              onChange: (page) => setTopProductsPage(page),
              position: ["bottomRight"],
              showSizeChanger: false,
            }}
            footer={() =>
              data?.topSellingProducts.totals ? (
                <div style={{ fontWeight: "bold" }}>
                  Totals: Quantity: {data.topSellingProducts.totals.quantity},
                  Revenue: {company.currencySymbol}
                  {data.topSellingProducts.totals.revenue}
                </div>
              ) : null
            }
          />
        </Card>
      )}
    </div>
  );
};

export default SalesReport;
